import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { formatInTimeZone } from 'date-fns-tz'
import { z } from 'zod'

import * as styles from './ReceiptForm.module.scss'

import {
  IssueReceiptRequest,
  ReservationReceiptResponse,
  ReservationResponse,
} from '../../../apis/fvillage'
import { TIMEZONE } from '../../../constants'
import {
  ButtonWrapper,
  FormInput,
  ReactHookFormErrorMessage,
} from '../../basics'
import { DetailList } from '../ReservationDetail/DetailList'

const translateNamespace = 'components.ReceiptForm'

type ReceiptFormProps = {
  isReceiptAvailable?: boolean
  onIssue: (addressee: string) => Promise<void>
  receipts?: ReservationResponse['receipts']
  issuedReceipt?: Pick<
    ReservationReceiptResponse,
    'id' | 'displayCount' | 'maxDisplayCount'
  >
  isReceiptReused?: boolean
  onDownload?: (id: number) => void
  isDownloading?: boolean
  downloadError?: boolean
  downloadLimitExceededReceiptId?: number
  onRetryDownload?: () => void
}

export const ReceiptForm: React.FC<ReceiptFormProps> = ({
  onIssue,
  isReceiptAvailable = false,
  receipts,
  issuedReceipt,
  isReceiptReused,
  onDownload,
  isDownloading,
  downloadError,
  downloadLimitExceededReceiptId,
  onRetryDownload,
}) => {
  const { t } = useTranslation()
  const schema = z.object({
    addressee: z
      .string()
      .trim()
      .min(1, t(`${translateNamespace}.required`))
      .max(255, t(`${translateNamespace}.tooLong`)),
  })
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<IssueReceiptRequest>({
    defaultValues: { addressee: '' },
    resolver: zodResolver(schema),
  })
  const onSubmit = handleSubmit(async ({ addressee }) => {
    if (!isReceiptAvailable) return
    try {
      await onIssue(addressee)
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === 400) {
        setError('addressee', {
          message: t(`${translateNamespace}.paymentRequired`),
        })
        return
      }
      if (status === 404) {
        setError('addressee', {
          message: t(`${translateNamespace}.unavailable`),
        })
        return
      }
      setError('addressee', { message: t(`${translateNamespace}.issueFailed`) })
    }
  })

  return (
    <DetailList
      header={t(`${translateNamespace}.title`)}
      listItems={[
        {
          key: 'receipt',
          items: [
            <div className={styles.content} key="receipt-form">
              <p>{t(`${translateNamespace}.description`)}</p>
              {!isReceiptAvailable && (
                <p className={styles.note}>
                  {t(`${translateNamespace}.availableAfterCheckout`)}
                </p>
              )}
              <form className={styles.form} onSubmit={onSubmit}>
                <fieldset
                  className={styles.fields}
                  disabled={!isReceiptAvailable || isSubmitting}
                >
                  <FormInput
                    control={control}
                    name="addressee"
                    label={t(`${translateNamespace}.addressee`)}
                  />
                  <ReactHookFormErrorMessage errors={errors} name="addressee" />
                  <ButtonWrapper
                    htmlType="submit"
                    position="center"
                    variant="primary"
                    size="medium"
                    disabled={!isReceiptAvailable || isSubmitting}
                  >
                    {t(
                      `${translateNamespace}.${
                        isSubmitting ? 'issuing' : 'issue'
                      }`
                    )}
                  </ButtonWrapper>
                </fieldset>
              </form>
              <div aria-live="polite">
                {isReceiptReused && (
                  <p className={styles.note}>
                    {t(`${translateNamespace}.alreadyIssued`)}
                  </p>
                )}
              </div>
              {receipts &&
                receipts.length > 0 &&
                receipts.map((receipt) => {
                  const maximum = receipt.maxDisplayCount ?? 5
                  const remaining = Math.max(
                    0,
                    maximum - (receipt.displayCount ?? 0)
                  )
                  return (
                    <fieldset
                      key={receipt.id}
                      className={styles.fields}
                      disabled={
                        !isReceiptAvailable ||
                        remaining === 0 ||
                        isDownloading ||
                        Boolean(downloadLimitExceededReceiptId)
                      }
                    >
                      <p>
                        {remaining === 0
                          ? t(`${translateNamespace}.limitReached`)
                          : t(`${translateNamespace}.remaining`, {
                              count: remaining,
                              maximum,
                            })}
                      </p>
                      <ButtonWrapper
                        htmlType="button"
                        position="center"
                        variant="secondary"
                        size="medium"
                        disabled={
                          !isReceiptAvailable ||
                          remaining === 0 ||
                          isDownloading ||
                          Boolean(downloadLimitExceededReceiptId)
                        }
                        onClick={() => {
                          if (isReceiptAvailable) onDownload?.(receipt.id)
                        }}
                      >
                        {t(`${translateNamespace}.existingReceipt`)}
                        <span className={styles.receiptDate}>
                          {formatInTimeZone(
                            receipt.paidAt,
                            TIMEZONE,
                            'yyyy/MM/dd HH:mm'
                          )}
                        </span>
                      </ButtonWrapper>
                    </fieldset>
                  )
                })}
              {issuedReceipt &&
                !receipts?.some(
                  (receipt) => receipt.id === issuedReceipt.id
                ) && (
                  <fieldset
                    className={styles.fields}
                    disabled={
                      !isReceiptAvailable ||
                      isDownloading ||
                      Boolean(downloadLimitExceededReceiptId) ||
                      (issuedReceipt.displayCount !== undefined &&
                        issuedReceipt.displayCount >=
                          (issuedReceipt.maxDisplayCount ?? 5))
                    }
                  >
                    {issuedReceipt.displayCount !== undefined && (
                      <p>
                        {t(`${translateNamespace}.remaining`, {
                          count: Math.max(
                            0,
                            (issuedReceipt.maxDisplayCount ?? 5) -
                              issuedReceipt.displayCount
                          ),
                          maximum: issuedReceipt.maxDisplayCount ?? 5,
                        })}
                      </p>
                    )}
                    <ButtonWrapper
                      htmlType="button"
                      position="center"
                      variant="secondary"
                      size="medium"
                      disabled={
                        !isReceiptAvailable ||
                        isDownloading ||
                        Boolean(downloadLimitExceededReceiptId) ||
                        (issuedReceipt.displayCount !== undefined &&
                          issuedReceipt.displayCount >=
                            (issuedReceipt.maxDisplayCount ?? 5))
                      }
                      onClick={() => {
                        if (isReceiptAvailable) onDownload?.(issuedReceipt.id)
                      }}
                    >
                      {t(`${translateNamespace}.openIssued`)}
                    </ButtonWrapper>
                  </fieldset>
                )}
              <div aria-live="polite">
                {isDownloading && <p>{t(`${translateNamespace}.preparing`)}</p>}
                {downloadError && (
                  <React.Fragment>
                    <p role="alert">
                      {t(
                        `${translateNamespace}.${
                          downloadLimitExceededReceiptId
                            ? 'downloadLimitReached'
                            : 'downloadFailed'
                        }`
                      )}
                    </p>
                    {!downloadLimitExceededReceiptId && (
                      <fieldset
                        className={styles.fields}
                        disabled={!isReceiptAvailable}
                      >
                        <ButtonWrapper
                          htmlType="button"
                          position="center"
                          variant="secondary"
                          size="medium"
                          disabled={!isReceiptAvailable}
                          onClick={() => {
                            if (isReceiptAvailable) onRetryDownload?.()
                          }}
                        >
                          {t(`${translateNamespace}.retryDownload`)}
                        </ButtonWrapper>
                      </fieldset>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>,
          ],
        },
      ]}
    />
  )
}
